import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Save, X, Plus, Trash2, ChevronDown, ChevronRight, Upload, Send, ThumbsUp, RotateCcw, Clock, Check, ArrowRight, FileText, Link, Download, AlertTriangle, ExternalLink, Copy, Loader2, Grid, Play, Pause } from 'lucide-react';
import api, { updateTask, uploadImage, uploadFile, getImageUrl, moveToQC, sendToClient, approveTask, requestRevisionClone, getTaskRevisions, uploadStoneData, assignCustomer, updateTaskStatus, getCadSheet, getExecutors, startTaskTimer, pauseTaskTimer, resetTaskTimer } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { STATUS_LABELS, STATUS_COLORS, STATUS_TRANSITIONS, PIECE_TYPES, METAL_TYPES, METAL_COLOURS, METAL_KARATS, FINDINGS_BY_PIECE_TYPE, STONE_SHAPES, SETTING_TYPES } from '@/utils/constants';
import AssignCustomerDialog from './AssignCustomerDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { TaskStoneImportDialog } from './TaskStoneImportDialog';
import { ClientSelect } from '@/lib/ClientSelect';
import { ImagesUploadedTable } from './ImagesUploadedTable';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Collapsible Section Component
const Section = ({ title, defaultOpen = true, children, badge }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-border rounded-lg bg-card mb-3" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
          <span className="text-sm font-semibold text-foreground">{title}</span>
          {badge && <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{badge}</span>}
        </div>
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-border/50">{children}</div>}
    </div>
  );
};

// Read-only field display
const Field = ({ label, value, className = '' }) => {
  if (!value && value !== 0) return null;
  return (
    <div className={className}>
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <p className="text-sm text-foreground mt-0.5 break-words">{value}</p>
    </div>
  );
};

// Editable field
const EditField = ({ label, value, onChange, type = 'text', placeholder = '', options = null, className = '', step, disabled = false }) => {
  if (options) {
    return (
      <div className={className}>
        <Label className="text-xs">{label}</Label>
        <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder={placeholder || `Select ${label}`} /></SelectTrigger>
          <SelectContent>{options.map(o => <SelectItem key={o.value || o} value={o.value || o}>{o.label || o}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    );
  }
  if (type === 'textarea') {
    return (
      <div className={className}>
        <Label className="text-xs">{label}</Label>
        <Textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={2} className="text-sm mt-1" disabled={disabled} />
      </div>
    );
  }
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} step={step} className="h-8 text-sm mt-1" disabled={disabled} />
    </div>
  );
};

const toLocalISOString = (utcString) => {
  if (!utcString) return '';
  if (typeof utcString === 'string' && !utcString.includes('Z') && !utcString.match(/[+-]\d{2}:\d{2}$/)) {
    return utcString.slice(0, 16).replace(' ', 'T');
  }
  const date = new Date(utcString);
  if (isNaN(date.getTime())) return '';
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().slice(0, 16);
};

const parseLocalISOString = (str) => {
  if (!str) return null;
  const matches = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!matches) {
    const d = new Date(str);
    return isNaN(d.getTime()) ? null : d;
  }
  const year = parseInt(matches[1], 10);
  const month = parseInt(matches[2], 10) - 1; // 0-indexed
  const day = parseInt(matches[3], 10);
  const hour = parseInt(matches[4], 10);
  const minute = parseInt(matches[5], 10);
  const second = matches[6] ? parseInt(matches[6], 10) : 0;
  return new Date(year, month, day, hour, minute, second);
};

const parseDateTime = (str) => {
  if (!str) return null;
  if (typeof str === 'string' && !str.includes('Z') && !str.match(/[+-]\d{2}:\d{2}$/)) {
    return parseLocalISOString(str);
  }
  return new Date(str);
};

const formatDateTime = (str) => {
  if (!str) return null;
  if (typeof str === 'string' && !str.includes('Z') && !str.match(/[+-]\d{2}:\d{2}$/)) {
    const parts = str.match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
    if (parts) {
      return `${parts[3]}-${parts[2]}-${parts[1]} ${parts[4]}:${parts[5]}`;
    }
  }
  const date = new Date(str);
  if (isNaN(date.getTime())) return str;
  const pad = (n) => String(n).padStart(2, '0');
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};


// CAD Sheet Preview Modal with Quick-Action Toolbar
const CadSheetPreviewModal = ({ data, task, onClose }) => {
  const { user } = useAuth();
  const captureRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [copied, setCopied] = useState(false);

  const styleNo = data.style_no || data.styleNo || task?.style_no || '';
  const title = styleNo || task?.internal_cad_no || 'CAD Sheet';

  const handleDownloadPng = async () => {
    if (!captureRef.current || downloading) return;
    setDownloading(true);
    try {
      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(captureRef.current, { pixelRatio: 3, backgroundColor: '#ffffff' });
      const link = document.createElement('a');
      link.download = `${title.replace(/[^a-zA-Z0-9-_]/g, '_')}_CAD.png`;
      link.href = dataUrl;
      link.click();
      toast.success('PNG downloaded');
    } catch (err) {
      console.error('PNG export failed:', err);
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleOpenInEditor = () => {
    const sheetId = data.id;
    if (sheetId) {
      window.open(`/cad-sheets?edit=${sheetId}`, '_blank');
    } else {
      window.open('/cad-sheets', '_blank');
    }
    toast.success('Opening CAD Editor...');
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/tasks?selected=${task?.id || ''}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error('Copy failed'));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="cad-sheet-preview-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-[920px] w-full max-h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header + Quick-Action Toolbar */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b bg-slate-50 rounded-t-xl flex-shrink-0">
          <h3 className="text-sm font-bold text-foreground truncate mr-3">
            CAD Sheet {title ? `\u2014 ${title}` : ''}
          </h3>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadPng}
              disabled={downloading}
              className="h-7 text-xs gap-1.5 border-teal-200 text-teal-700 hover:bg-teal-50"
              data-testid="cad-preview-download-png"
            >
              {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              Download PNG
            </Button>
            {user?.role !== 'customer' && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleOpenInEditor}
                className="h-7 text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                data-testid="cad-preview-open-editor"
              >
                <ExternalLink size={13} />
                Open in Editor
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleCopyLink}
              className={`h-7 text-xs gap-1.5 transition-colors ${copied ? 'border-green-300 text-green-700 bg-green-50' : 'border-border text-muted-foreground hover:bg-muted/50'}`}
              data-testid="cad-preview-copy-link"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? 'Copied!' : 'Copy Link'}
            </Button>
            <div className="w-px h-5 bg-border mx-1" />
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}><X size={16} /></Button>
          </div>
        </div>

        {/* Scrollable content — captured for PNG */}
        <div className="flex-1 overflow-auto p-4">
          <div ref={captureRef} className="bg-white">
            {data._fallback ? (
              <div className="text-center py-4">
                {data.cad_image && <img src={getImageUrl(data.cad_image)} alt="CAD" className="max-w-full max-h-[65vh] object-contain mx-auto rounded border" />}
                <p className="text-xs text-muted-foreground mt-3">Version {data.version} — Full sheet data not available</p>
              </div>
            ) : (
              <div className="flex gap-4">
                {/* LEFT — Views */}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-muted-foreground mb-2">Views</p>
                  <div className="grid grid-cols-2 gap-2">
                    {(data.images || []).map((img, idx) => {
                      const src = img.base64 || img.url;
                      return src ? (
                        <div key={idx} className="border rounded-lg overflow-hidden bg-white shadow-sm">
                          <img src={src} alt={img.label || `View ${idx+1}`} className="w-full aspect-square object-contain bg-slate-50" />
                          <p className="text-[10px] text-center py-1 bg-slate-50 text-muted-foreground font-medium border-t">{img.label || `View ${idx+1}`}</p>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
                {/* RIGHT — Data */}
                <div className="w-[270px] flex-shrink-0 text-xs space-y-3">
                  <div>
                    <p className="font-bold text-sm text-foreground">{data.style_no || data.styleNo || 'Untitled'}</p>
                    <p className="text-muted-foreground">{data.jewelry_type || data.jewelryType}{data.size ? ` | Size: ${data.size}` : ''}</p>
                    {(data.customer_name || data.customerName) && <p className="text-muted-foreground">Customer: {data.customer_name || data.customerName}</p>}
                    {(data.customer_design_no || data.customerDesignNo) && <p className="text-muted-foreground">Design No: {data.customer_design_no || data.customerDesignNo}</p>}
                    {(data.designer_name || data.designerName) && (
                      <p className="text-muted-foreground">
                        Designer: #{ (data.designer_name || data.designerName).replace(/[^a-zA-Z]/g, '').slice(0, 3) }
                      </p>
                    )}
                    {data.date && <p className="text-muted-foreground">Date: {data.date}</p>}
                  </div>
                  {(data.metals || []).length > 0 && (
                    <div>
                      <p className="font-semibold border-b border-border pb-0.5 mb-1">Metal & Weight</p>
                      {data.metals.map((m, i) => <p key={i}>{m.karat} {m.color}{m.netWeight ? ` (${m.netWeight}g)` : ''}</p>)}
                      {(data.net_weight || data.netWeight || data.gross_weight || data.grossWeight) && (
                        <p className="text-muted-foreground mt-0.5">
                          {(data.net_weight || data.netWeight) && `Net: ${data.net_weight || data.netWeight}g`}
                          {(data.gross_weight || data.grossWeight) && ` | Gross: ${data.gross_weight || data.grossWeight}g`}
                        </p>
                      )}
                    </div>
                  )}
                  {(data.stones || []).length > 0 && (
                    <div>
                      <p className="font-semibold border-b border-border pb-0.5 mb-1">Stones ({data.stones.length})</p>
                      <table className="w-full text-[10px]">
                        <thead><tr className="border-b border-border"><th className="text-left p-0.5">Stone</th><th className="text-left p-0.5">Own.</th><th className="text-left p-0.5">Cut</th><th className="text-left p-0.5">Qty</th><th className="text-left p-0.5">Ct.Wt</th></tr></thead>
                        <tbody>{data.stones.map((s, i) => (
                          <tr key={i} className="border-b border-border/30">
                            <td className="p-0.5">{s.stone_category || '-'}</td>
                            <td className="p-0.5">{s.ownership || '-'}</td>
                            <td className="p-0.5">{s.stone_shape || '-'}</td>
                            <td className="p-0.5">{s.quantity || '-'}</td>
                            <td className="p-0.5">{s.weight_per_stone || '-'}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  )}
                  {(data.findings || []).filter(f => f.type).length > 0 && (
                    <div>
                      <p className="font-semibold border-b border-border pb-0.5 mb-1">Findings</p>
                      {data.findings.filter(f => f.type).map((f, i) => (
                        <p key={i}>{f.type}{f.weight ? ` \u2014 ${f.weight}g` : ''}{f.details ? ` (${f.details})` : ''}</p>
                      ))}
                    </div>
                  )}
                  {(data.finishing || data.finishing) && <p><span className="font-semibold">Finishing:</span> {data.finishing}</p>}
                  {(data.engraving_instructions || data.engravingInstructions) && <p><span className="font-semibold">Engraving:</span> {data.engraving_instructions || data.engravingInstructions}</p>}
                  {(data.other_details || data.otherDetails) && <p><span className="font-semibold">Notes:</span> {data.other_details || data.otherDetails}</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const formatHMS = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
};

const TimerCard = ({ task, onUpdate, isAdmin }) => {
  const segments = Array.isArray(task?.time_segments) ? task.time_segments : [];
  const activeId = task?.active_segment_id || null;
  const active = activeId ? segments.find(s => s.id === activeId) : null;
  const accumulatedSec = segments.reduce((sum, s) => sum + (s.seconds || 0), 0);

  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);
  const [showSegments, setShowSegments] = useState(false);
  const tickRef = useRef(null);

  useEffect(() => {
    if (active && active.start_at) {
      tickRef.current = setInterval(() => setNow(Date.now()), 1000);
      return () => { if (tickRef.current) clearInterval(tickRef.current); };
    }
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
  }, [active?.id, active?.start_at]);

  const liveExtra = active && active.start_at
    ? Math.max(0, (now - new Date(active.start_at).getTime()) / 1000)
    : 0;
  const displaySec = accumulatedSec + liveExtra;

  const handleToggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (active) {
        await pauseTaskTimer(task.id);
        toast.success('Timer paused');
      } else {
        await startTaskTimer(task.id);
        toast.success('Timer started');
      }
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Timer action failed');
    } finally {
      setBusy(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('This will clear all time logged. Continue?')) return;
    setBusy(true);
    try {
      await resetTaskTimer(task.id);
      toast.success('Timer reset');
      onUpdate?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-border rounded-lg bg-muted/20 p-4 flex flex-col items-center gap-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Work Timer</div>
      <div className={`font-mono text-4xl font-bold tabular-nums ${active ? 'text-green-600' : 'text-foreground'}`}>
        {formatHMS(displaySec)}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={handleToggle}
          disabled={busy}
          className={active ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-green-600 hover:bg-green-700 text-white'}
        >
          {active ? <><Pause size={14} className="mr-1" /> Pause</> : <><Play size={14} className="mr-1" /> Start</>}
        </Button>
        {isAdmin && (
          <Button type="button" size="sm" variant="outline" onClick={handleReset} disabled={busy}>
            <RotateCcw size={14} className="mr-1" /> Reset
          </Button>
        )}
      </div>
      {active && active.started_by_name && (
        <div className="text-[10px] text-muted-foreground">Running since {new Date(active.start_at).toLocaleTimeString()} by {active.started_by_name}</div>
      )}
      {segments.length > 0 && (
        <button type="button" onClick={() => setShowSegments(s => !s)} className="text-[10px] text-muted-foreground hover:text-foreground underline">
          {showSegments ? 'Hide' : 'Show'} {segments.length} segment{segments.length === 1 ? '' : 's'}
        </button>
      )}
      {showSegments && (
        <div className="w-full max-h-40 overflow-y-auto text-[11px] border-t border-border pt-2 space-y-1">
          {segments.map((s, i) => (
            <div key={s.id || i} className="flex justify-between gap-2">
              <span className="text-muted-foreground">
                {s.start_at ? new Date(s.start_at).toLocaleString() : '—'}
                {' → '}
                {s.end_at ? new Date(s.end_at).toLocaleTimeString() : <span className="text-green-600">running</span>}
              </span>
              <span className="font-mono">{s.seconds != null ? formatHMS(s.seconds) : '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const TaskSummaryView = ({ task, onUpdate }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [revisionRemarks, setRevisionRemarks] = useState('');
  const [showRevisionInput, setShowRevisionInput] = useState(false);
  const [revisionImages, setRevisionImages] = useState([]);
  const [uploadingRevisionImage, setUploadingRevisionImage] = useState(false);
  const [showAssignCustomer, setShowAssignCustomer] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showExportModal, setShowExportModal] = useState(false);
  const [cadSheetPreview, setCadSheetPreview] = useState(null); // {images: [...], data: {...}} for modal
  const [showBulkStoneDialog, setShowBulkStoneDialog] = useState(false);

  const handleBulkStoneImport = (importedStones) => {
    updateField('diamonds', importedStones);
    toast.success(`${importedStones.length} stones imported`);
  };

  const isInternal = user?.role === 'admin' || user?.role === 'executor' || user?.role === 'special_executor' || user?.role === 'final_executor';
  const isExecutorOrFinalExecutor = user?.role === 'executor' || user?.role === 'final_executor';
  const canEdit = user?.role === 'admin' || user?.role === 'special_executor' || user?.role === 'executor' || user?.role === 'customer';
  const isCustomer = user?.role === 'customer';
  const currentStatus = task?.design_status || 'cad_in_progress';
  const statusLabel = STATUS_LABELS[currentStatus] || currentStatus;
  const statusColor = STATUS_COLORS[currentStatus] || 'bg-muted text-foreground';
  const hasRevisions = task?.revision_suffix || task?.parent_task_id || (task?.client_revision_count > 0);

  const [executors, setExecutors] = useState([]);

  useEffect(() => {
    setRevisions([]);
    setRevisionRemarks('');
    setShowRevisionInput(false);
    setRevisionImages([]);
    setCadSheetPreview(null);
    if (task && hasRevisions) {
      getTaskRevisions(task.id).then(r => setRevisions(r.data || [])).catch(() => {});
    }
  }, [task?.id, hasRevisions]);

  // Fetch executors for designer dropdown
  useEffect(() => {
    getExecutors()
      .then(res => setExecutors(res.data || []))
      .catch(() => setExecutors([]));
  }, []);

  const startEdit = () => {
    setEditData({ ...task });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };

  const updateField = (key, value) => {
    setEditData(prev => {
      const updated = { ...prev, [key]: value };
      // Auto-update file name when style_no changes
      if (key === 'style_no') updated.cad_file_name = value;
      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { ...editData };
      // Internal / read-only / server-managed fields — never send back.
      // Time-tracking fields are mutated via /timer endpoints, not PUT.
      // Audit/timestamp/lineage fields would overwrite server values.
      const STRIP = [
        '_id', 'id', 'created_at', 'updated_at',
        'cad_versions', 'client_revisions', 'parent_task_id',
        'revision_suffix', 'unread_by', 'messages',
        'client_revision_count', 'workflow_signoffs', 'status_logs',
        'time_segments', 'active_segment_id',
        'designer_hashtag', 'quotation_id', 'quotation_ref',
        'cad_completion_time_local', 'cad_start_time_local',
        // Set only by the SGI import flow — round-tripping through a task
        // edit risks Pydantic-422 on rows whose image_url scrape missed.
        'image_notes',
        // Server-managed lineage / audit / qc state — must never be set via
        // the summary edit. Each has its own endpoint.
        'import_source', 'qc_checklist', 'last_modified_by',
      ];
      for (const k of STRIP) delete data[k];

      // Numeric coercion — empty strings, "abc", NaN all become undefined so
      // the field is OMITTED below (server keeps existing value) instead of
      // sent as a string that Pydantic would 422 on.
      const numFields = ['design_points', 'cad_points', 'actual_time_hours', 'net_weight', 'gross_weight'];
      const intFields = ['quantity'];
      for (const k of numFields) {
        if (data[k] === '' || data[k] === null || data[k] === undefined) { delete data[k]; continue; }
        const n = parseFloat(data[k]);
        data[k] = Number.isFinite(n) ? n : undefined;
        if (data[k] === undefined) delete data[k];
      }
      for (const k of intFields) {
        if (data[k] === '' || data[k] === null || data[k] === undefined) { delete data[k]; continue; }
        const n = parseInt(data[k], 10);
        data[k] = Number.isFinite(n) ? n : undefined;
        if (data[k] === undefined) delete data[k];
      }

      // Datetime-local inputs produce "" when cleared. Backend treats "" as
      // an invalid datetime string for these fields and rejects the PUT.
      const dateFields = ['target_date', 'deadline', 'start_date', 'end_date', 'cad_start_time', 'cad_completion_time', 'order_date'];
      for (const k of dateFields) {
        if (data[k] === '' || data[k] === null) delete data[k];
      }

      // Sanitize nested arrays — drop rows that are completely empty so the
      // user doesn't accidentally send {weight:""} which fails float coercion.
      // Nested-array normalization. Strict coercion: empty strings on numeric
      // fields become 0 (not "") so Pydantic doesn't choke on type. Backend
      // models are now lenient (all row fields Optional with defaults), but we
      // still drop rows where every value is empty to keep stored data clean.
      const toFloat = (v) => {
        if (v === '' || v == null) return 0;
        const n = parseFloat(v);
        return Number.isFinite(n) ? n : 0;
      };
      const toInt = (v) => {
        if (v === '' || v == null) return 0;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : 0;
      };
      if (Array.isArray(data.metal_details)) {
        data.metal_details = data.metal_details
          .map(m => ({
            ...m,
            metal_type: m.metal_type || '',
            metal_karat: m.metal_karat || '',
            metal_colour: m.metal_colour || '',
            weight: m.weight === '' || m.weight == null ? null : toFloat(m.weight),
          }))
          .filter(m => m.metal_type || m.metal_karat || m.metal_colour || (m.weight != null && m.weight > 0));
      }
      if (Array.isArray(data.diamonds)) {
        data.diamonds = data.diamonds
          .map(s => ({
            ...s,
            stone_shape: s.stone_shape || '',
            quantity: toInt(s.quantity),
            weight_per_stone: toFloat(s.weight_per_stone),
            total_weight: toFloat(s.total_weight ?? (toInt(s.quantity) * toFloat(s.weight_per_stone))),
          }))
          .filter(s => s.stone_shape || s.weight_per_stone > 0 || s.quantity > 0 || s.stone_quality);
      }
      if (Array.isArray(data.other_stones)) {
        data.other_stones = data.other_stones
          .map(s => ({
            ...s,
            stone_name: s.stone_name || '',
            shape: s.shape || '',
            size_mm: s.size_mm || '',
            pieces: toInt(s.pieces),
            weight_ct: toFloat(s.weight_ct),
          }))
          .filter(s => s.stone_name || s.shape || s.weight_ct > 0 || s.pieces > 0);
      }
      if (Array.isArray(data.findings)) {
        data.findings = data.findings
          .map(f => ({
            ...f,
            weight: f.weight === '' || f.weight == null ? null : toFloat(f.weight),
          }))
          .filter(f => f.name || f.code || f.custom_name || (f.weight != null && f.weight > 0));
      }

      // Drop null/undefined/empty-string scalars so we don't blank server fields.
      const cleanData = {};
      for (const [k, v] of Object.entries(data)) {
        if (v === undefined || v === null) continue;
        if (typeof v === 'string' && v === '') continue;
        cleanData[k] = v;
      }

      // Surface the payload in the console so if the backend still rejects it
      // we can see exactly what was sent without bouncing to network DevTools.
      if (process.env.NODE_ENV !== 'production') console.debug('[task save payload]', cleanData);

      await updateTask(task.id, cleanData);
      toast.success('Saved');
      setEditMode(false);
      onUpdate?.();
    } catch (err) {
      console.error('Save failed:', err);
      const detail = err.response?.data?.detail;
      // Pydantic 422 returns an array of {loc, msg, type}. Surface the first
      // few in a readable form so the user can see which field/row failed
      // (was previously "[object Object]" or a stringified blob).
      let msg = 'Save failed';
      if (typeof detail === 'string' && detail.trim()) {
        msg = detail;
      } else if (Array.isArray(detail) && detail.length) {
        msg = detail.slice(0, 3).map(e => {
          const field = (e.loc || []).slice(1).join('.') || 'body';
          return `${field}: ${e.msg || e.type || 'invalid'}`;
        }).join(' · ');
      } else if (err.message) {
        msg = err.message;
      }
      toast.error(msg, { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadImage(file);
      updateField(field, res.data.image);
      toast.success('Image uploaded');
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Upload failed');
    }
  };

  const handleFileUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const isImage = file.type.startsWith('image/');
      const res = isImage ? await uploadImage(file) : await uploadFile(file);
      const path = res.data.image || res.data.url || res.data.path;
      updateField(field, path);
      toast.success('File uploaded');
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Upload failed');
    }
  };

  const handleRefImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    const current = editData.reference_images || [];
    for (const file of files) {
      try {
        const res = await uploadImage(file);
        current.push(res.data.image);
      } catch (err) { console.error('Ref upload failed:', err); }
    }
    updateField('reference_images', [...current]);
  };

  const handleExportSummaryPDF = async () => {
    try {
      toast.info('Generating PDF Summary...');
      const response = await api.get(`/api/tasks/${task.id}/export-pdf?include_summary=true&include_cad=false`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cad_no = task.internal_cad_no || task.party_cad_no || 'CAD';
      link.setAttribute('download', `${cad_no}_Summary.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded PDF Summary');
    } catch (err) {
      console.error('PDF export failed:', err);
      toast.error('Failed to generate PDF Summary');
    }
  };

  const handleExportSummaryExcel = async () => {
    try {
      toast.info('Generating Excel Summary...');
      const response = await api.get(`/api/tasks/${task.id}/export?format=excel`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cad_no = task.internal_cad_no || task.party_cad_no || 'CAD';
      link.setAttribute('download', `${cad_no}_Summary.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded Excel Summary');
    } catch (err) {
      console.error('Excel export failed:', err);
      toast.error('Failed to generate Excel Summary');
    }
  };

  const handleExportModelPaper = async () => {
    try {
      toast.info('Generating Model Paper...');
      const response = await api.get(`/api/tasks/${task.id}/export-model-paper`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cad_no = task.internal_cad_no || task.party_cad_no || 'CAD';
      link.setAttribute('download', `${cad_no}_Model_Paper.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Downloaded Model Paper');
    } catch (err) {
      console.error('Model Paper export failed:', err);
      toast.error('Failed to generate Model Paper');
    }
  };

  // Workflow actions
  const handleMoveToQC = async () => {
    try {
      await moveToQC(task.id);
      toast.success('Moved to QC Review');
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleSendToClient = async () => {
    try {
      await sendToClient(task.id);
      toast.success('Sent to Client');
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleApprove = async () => {
    try {
      await approveTask(task.id);
      toast.success('Approved');
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleStatusChange = async (newStatus, newPriority) => {
    try {
      if (newPriority) {
        await updateTask(task.id, { priority: newPriority });
        toast.success(newPriority === 'super_rush' ? '⚠️ SUPER RUSH!' : 'Normal priority');
        onUpdate?.();
        return;
      }
      await updateTaskStatus(task.id, { status: newStatus });
      toast.success(`Status changed to ${STATUS_LABELS[newStatus] || newStatus}`);
      onUpdate?.();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const [submittingRevision, setSubmittingRevision] = useState(false);

  const handleRequestRevision = async () => {
    if (!revisionRemarks.trim()) { toast.error('Please add remarks'); return; }
    if (submittingRevision) return; // Debounce
    setSubmittingRevision(true);
    try {
      const res = await requestRevisionClone(task.id, { remarks: revisionRemarks, images: revisionImages });
      const revNum = res.data.revision_number;
      const newTaskId = res.data.new_task_id;
      toast.success(`Revision ${revNum || ''} created`);
      setShowRevisionInput(false);
      setRevisionRemarks('');
      setRevisionImages([]);
      onUpdate?.();
      // Navigate to the newly-created revision task so the middle pane reloads
      // against the child's CAD files / data instead of the master's.
      if (newTaskId) {
        navigate(`/tasks?selected=${newTaskId}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmittingRevision(false); }
  };

  const handleRevisionImageUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingRevisionImage(true);
    try {
      const urls = [];
      for (const file of files) {
        const res = await uploadImage(file);
        // Backend returns {image: "/api/uploads/..."} for uploadImage
        const imgPath = res.data.image || res.data.url || res.data.filename;
        if (imgPath) urls.push(imgPath);
      }
      if (urls.length > 0) setRevisionImages(prev => [...prev, ...urls]);
    } catch (err) {
      console.error('Revision image upload failed:', err);
      toast.error('Image upload failed');
    }
    finally { setUploadingRevisionImage(false); }
    e.target.value = '';
  };

  const handleStoneUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadStoneData(task.id, file);
      toast.success(`${res.data.count || 0} stones imported`);
      onUpdate?.();
    } catch { toast.error('Stone import failed'); }
  };

  const handleOpenCadSheet = () => {
    const latestVersion = task?.cad_versions && task.cad_versions.length > 0 
      ? [...task.cad_versions].reverse().find(v => v.sheet_id)
      : null;
      
    if (latestVersion?.sheet_id) {
      window.open(`/cad-sheets?edit=${latestVersion.sheet_id}`, '_blank');
      toast.success('Opening existing CAD sheet...');
    } else {
      window.open(`/cad-sheets?task=${task.id}`, '_blank');
      toast.success('Opening CAD Designer with preloaded task details...');
    }
  };

  // Stone CRUD in edit mode
  const addStone = () => {
    const stones = [...(editData.diamonds || []), { category: 'Accent Stone', stone_category: 'Natural', ownership: 'KS Goods', stone_shape: 'Round', stone_quality: '', quantity: 1, size_mm: '', weight_per_stone: 0, total_weight: 0, setting_type: 'Prong' }];
    updateField('diamonds', stones);
  };
  const updateStone = (idx, field, value) => {
    const stones = [...(editData.diamonds || [])];
    stones[idx] = { ...stones[idx], [field]: value };
    if (field === 'quantity' || field === 'weight_per_stone') {
      stones[idx].total_weight = (stones[idx].quantity || 0) * (stones[idx].weight_per_stone || 0);
    }
    updateField('diamonds', stones);
  };
  const removeStone = (idx) => {
    updateField('diamonds', (editData.diamonds || []).filter((_, i) => i !== idx));
  };

  // Findings CRUD
  const addFinding = () => {
    updateField('findings', [...(editData.findings || []), { name: '', custom_name: '', code: '', weight: 0, details: '' }]);
  };
  const updateFinding = (idx, field, value) => {
    const findings = [...(editData.findings || [])];
    findings[idx] = { ...findings[idx], [field]: value };
    // Auto-fill weight from template
    if (field === 'code') {
      const pieceFindings = FINDINGS_BY_PIECE_TYPE[editData.piece_type] || [];
      const match = pieceFindings.find(f => f.code === value);
      if (match) {
        findings[idx].name = match.name;
        const karat = editData.metal_karat || '14K';
        findings[idx].weight = match.weights?.[karat] || 0;
      }
    }
    updateField('findings', findings);
  };
  const removeFinding = (idx) => {
    updateField('findings', (editData.findings || []).filter((_, i) => i !== idx));
  };

  if (!task) return <div className="flex items-center justify-center h-full text-muted-foreground text-sm">Select a task to view details</div>;

  const d = editMode ? editData : task;
  // The lineage root — every revision in the lineage shares this id as its
  // parent_task_id; "Current" tab always navigates to it.
  const rootTaskId = task?.parent_task_id || task?.id;
  const metalColors = METAL_COLOURS[d.metal_type] || ['Yellow', 'White', 'Rose'];
  const metalKarats = METAL_KARATS[d.metal_type] || ['14K', '18K', '22K'];
  const pieceFindings = FINDINGS_BY_PIECE_TYPE[d.piece_type] || [];
  const totalDiamondWeight = (d.diamonds || []).reduce((s, st) => s + (parseFloat(st.total_weight) || 0), 0);
  const totalFindingsWeight = (d.findings || []).reduce((s, f) => s + (parseFloat(f.weight) || 0), 0);
  const grossWtNum = parseFloat(d.gross_weight) || 0;
  const totalWeight = grossWtNum > 0 ? (grossWtNum + totalFindingsWeight).toFixed(2) : '';

  return (
    <div className="h-full flex flex-col bg-background" data-testid="task-summary-view">
      {/* Status Banner with Dropdown */}
      <div className={`mx-4 mt-3 px-4 py-2.5 rounded-lg border flex flex-wrap items-center justify-between gap-2 ${statusColor}`} data-testid="status-banner">
        <div className="flex items-center gap-3 flex-wrap">
          {isInternal ? (
            <Select value={currentStatus} onValueChange={handleStatusChange} data-testid="status-dropdown">
              <SelectTrigger className={`h-7 text-sm font-bold border-0 bg-transparent shadow-none px-0 w-auto gap-1.5 ${statusColor.includes('blue') ? 'text-blue-700' : statusColor.includes('green') ? 'text-green-700' : statusColor.includes('rose') ? 'text-rose-700' : statusColor.includes('orange') ? 'text-orange-700' : 'text-foreground'}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={currentStatus}>{STATUS_LABELS[currentStatus] || currentStatus}</SelectItem>
                {(STATUS_TRANSITIONS[currentStatus] || []).map(s => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="text-sm font-bold">{statusLabel}</span>
          )}
          {(d.priority === 'super_rush' || d.priority === 'urgent') && <span className="text-xl text-red-500" title="SUPER RUSH" data-testid="priority-badge"><AlertTriangle size={18} className="inline fill-red-100 mr-1" /></span>}
          {d.designer_name && <span className="text-xs opacity-75">Designer: {d.designer_name}{d.designer_hashtag ? ` ${d.designer_hashtag}` : ''}</span>}
        </div>
        <div className="flex items-center gap-2 text-xs flex-wrap">
          {d.cad_points && <span className="font-medium">Points: {d.cad_points}h</span>}
          {d.actual_time_hours && <span>Actual: {d.actual_time_hours}h</span>}
          {d.start_date && <span>Started: {new Date(d.start_date).toLocaleDateString()}</span>}
          {d.target_date && <span className="font-semibold">Due: {new Date(d.target_date).toLocaleDateString()}</span>}
        </div>
      </div>

      {/* Prominent Revision Remarks Banner */}
      {(d.client_revisions?.length > 0) && (() => {
        const latest = d.client_revisions[d.client_revisions.length - 1];
        return (
          <div className="mx-4 mt-2 px-4 py-3 rounded-lg border-2 border-rose-300 bg-rose-50" data-testid="revision-summary-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-rose-700">Revision #{latest.revision_number || d.client_revision_count || '?'} Remarks</span>
                  <span className="text-[10px] text-rose-500">{latest.requested_at ? new Date(latest.requested_at).toLocaleString() : ''}</span>
                  {latest.requested_by_name && <span className="text-[10px] text-rose-500">by {latest.requested_by_name}</span>}
                </div>
                <p className="text-sm text-rose-900 font-medium">{latest.remarks || 'No remarks provided'}</p>
                {(latest.images || []).length > 0 && (
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {latest.images.filter(Boolean).map((img, i) => (
                      <a key={i} href={getImageUrl(img)} target="_blank" rel="noreferrer">
                        <img src={getImageUrl(img)} alt={`rev-${i}`} className="w-16 h-16 rounded border-2 border-rose-200 object-cover hover:ring-2 ring-rose-400 transition-all" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-card flex-shrink-0">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-foreground truncate" data-testid="task-title">{d.style_no || d.internal_cad_no || 'Untitled'}</h2>
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${statusColor}`} data-testid="task-status">{statusLabel}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                {d.internal_cad_no}{d.party_cad_no ? ` / ${d.party_cad_no}` : ''}{d.client_name ? ` \u2014 ${d.client_name}` : ''}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {editMode ? (
              <>
                <Button type="button" size="sm" variant="ghost" onClick={cancelEdit} className="h-7 text-xs"><X size={14} className="mr-1" />Cancel</Button>
                <Button type="button" size="sm" onClick={handleSave} disabled={saving} className="h-7 text-xs" data-testid="save-btn"><Save size={14} className="mr-1" />{saving ? 'Saving...' : 'Save'}</Button>
              </>
            ) : (
              <>
                {(isInternal || user?.role === 'customer') && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" data-testid="export-summary-dropdown">
                        <Download size={14} className="mr-1" />Export Summary <ChevronDown size={12} className="ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleExportSummaryPDF}>PDF Format</DropdownMenuItem>
                      <DropdownMenuItem onClick={handleExportSummaryExcel}>Excel Format</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}

                {isInternal && (
                  <Button type="button" size="sm" variant="outline" onClick={handleExportModelPaper} className="h-7 text-xs" data-testid="export-model-paper-btn">
                    <FileText size={14} className="mr-1" />Model Paper
                  </Button>
                )}
                {isInternal && user?.role !== 'final_executor' && (
                  <Button 
                    type="button" 
                    size="sm" 
                    onClick={handleOpenCadSheet} 
                    className="h-7 text-xs bg-teal-600 hover:bg-teal-700 text-white" 
                    data-testid="open-cad-sheet-btn"
                  >
                    <Grid size={14} className="mr-1" />Open CAD Sheet
                  </Button>
                )}
                {canEdit && <Button type="button" size="sm" variant="outline" onClick={startEdit} className="h-7 text-xs" data-testid="edit-btn"><Edit size={14} className="mr-1" />Edit</Button>}
              </>
            )}
          </div>
        </div>

        {/* Workflow Actions — Pre-CAD → CAD in Progress → CAD Completed → Accept/Reject/Revision */}
        {!editMode && (
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {/* Priority toggle */}
            <button onClick={() => handleStatusChange(undefined, d.priority === 'super_rush' ? 'normal' : 'super_rush')}
              className="h-7 px-2 rounded border text-xs inline-flex items-center gap-1 hover:bg-muted/50 transition-colors" data-testid="priority-toggle">
              {(d.priority === 'super_rush' || d.priority === 'urgent') ? <><AlertTriangle size={12} className="text-red-500 fill-red-100" /> SUPER RUSH</> : <>Normal</>}
            </button>

            {/* CAD Completed → Accept, Reject or Request Revision */}
            {(currentStatus === 'cad_completed' || currentStatus === 'sent_to_client' || currentStatus === 'qc_review') && (
              <>
                <Button size="sm" onClick={() => handleStatusChange('accepted')} className="h-7 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid="accept-btn">
                  <Check size={12} className="mr-1" />Accept
                </Button>
                <Button size="sm" onClick={() => handleStatusChange('rejected')} className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="reject-btn">
                  <X size={12} className="mr-1" />Reject
                </Button>
                {!showRevisionInput && (
                  <Button size="sm" variant="outline" onClick={() => setShowRevisionInput(true)} className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="request-revision-btn">
                    <RotateCcw size={12} className="mr-1" />Revision
                  </Button>
                )}
              </>
            )}

            {/* Accepted → allow revision or rejection */}
            {(currentStatus === 'accepted' || currentStatus === 'approved' || currentStatus === 'client_approved') && (
              <>
                {!showRevisionInput && (
                  <Button size="sm" variant="outline" onClick={() => setShowRevisionInput(true)} className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="request-revision-btn">
                    <RotateCcw size={12} className="mr-1" />Request Revision
                  </Button>
                )}
                <Button size="sm" onClick={() => handleStatusChange('rejected')} className="h-7 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="reject-from-accepted-btn">
                  <X size={12} className="mr-1" />Reject
                </Button>
              </>
            )}

            {/* Rejected → allow revision or back to CAD */}
            {currentStatus === 'rejected' && (
              <>
                {!showRevisionInput && (
                  <Button size="sm" variant="outline" onClick={() => setShowRevisionInput(true)} className="h-7 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="request-revision-btn">
                    <RotateCcw size={12} className="mr-1" />Request Revision
                  </Button>
                )}
                <Button size="sm" onClick={() => handleStatusChange('cad_in_progress')} className="h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="back-to-cad-btn">
                  Back to CAD
                </Button>
              </>
            )}
          </div>
        )}

        {/* Revision Input */}
        {showRevisionInput && (
          <div className="mt-2 space-y-2">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">Revision Remarks</Label>
                <Input value={revisionRemarks} onChange={e => setRevisionRemarks(e.target.value)} placeholder="Describe changes needed..." className="h-8 text-sm" data-testid="revision-remarks-input" />
              </div>
              <Button size="sm" onClick={handleRequestRevision} disabled={submittingRevision} className="h-8 text-xs bg-rose-600 hover:bg-rose-700 text-white" data-testid="submit-revision-btn">{submittingRevision ? 'Creating...' : 'Submit'}</Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowRevisionInput(false); setRevisionImages([]); }} className="h-8 text-xs">Cancel</Button>
            </div>
            {/* Revision Image Upload */}
            <div className="flex items-center gap-2 flex-wrap">
              <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded border border-dashed border-rose-300 text-[11px] text-rose-600 hover:bg-rose-50 transition-colors" data-testid="revision-image-upload-label">
                <Upload size={12} />
                {uploadingRevisionImage ? 'Uploading...' : 'Attach Images'}
                <input type="file" accept="image/*" multiple onChange={handleRevisionImageUpload} className="hidden" data-testid="revision-image-upload-input" />
              </label>
              {revisionImages.map((img, i) => (
                <div key={i} className="relative group">
                  <img src={getImageUrl(img)} alt={`rev-${i}`} className="w-10 h-10 rounded border object-cover" />
                  <button onClick={() => setRevisionImages(prev => prev.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[8px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <X size={8} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 mt-3 border-b border-border -mx-4 px-4">
          {[
            { id: 'details', label: 'CAD Details' },
            { id: 'workflow', label: 'Workflow', roles: ['admin', 'executor', 'special_executor', 'final_executor'] },
            { id: 'client', label: 'Client' },
          ].filter(tab => !tab.roles || tab.roles.includes(user?.role)).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${
                activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
          {/* Revision toggle tabs — Current (= master) + every child rev.
              Clicking navigates to that task; the middle pane re-renders
              against the new task instead of swapping data inline. */}
          {revisions.length > 0 && (
            <>
              <div className="w-px h-4 bg-border mx-1" />
              <span className="text-[10px] text-muted-foreground mr-1">Revisions:</span>
              <button
                onClick={() => { if (task.id !== rootTaskId) navigate(`/tasks?selected=${rootTaskId}`); }}
                className={`px-2 py-1 text-[10px] font-medium rounded-t border-b-2 transition-colors ${
                  task.id === rootTaskId ? 'border-teal-500 text-teal-700 bg-teal-50' : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                data-testid="revision-tab-current"
              >
                Current
              </button>
              {revisions.map((rev, i) => (
                <button
                  key={rev.id}
                  onClick={() => { if (rev.id !== task.id) navigate(`/tasks?selected=${rev.id}`); }}
                  className={`px-2 py-1 text-[10px] font-medium rounded-t border-b-2 transition-colors ${
                    task.id === rev.id ? 'border-rose-400 text-rose-700 bg-rose-50' : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid={`revision-tab-${i}`}
                >
                  Rev {rev.revision_suffix || i + 1}
                </button>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'details' && (
          <div>
            {/* Hero Product Image + CAD Image at Top */}
            <div className="mb-4 flex gap-4 justify-center flex-wrap">
              {d.product_image && (
                <div data-testid="hero-product-image">
                  <p className="text-[10px] text-muted-foreground text-center mb-1">Reference</p>
                  <div className="relative border border-border rounded-lg overflow-hidden bg-white shadow-sm">
                    <img src={getImageUrl(d.product_image)} alt="Product" className="max-w-[180px] max-h-[180px] object-contain p-2" />
                  </div>
                </div>
              )}
              {d.cad_image && (
                <div data-testid="hero-cad-image">
                  <p className="text-[10px] text-muted-foreground text-center mb-1">Latest CAD</p>
                  <div className="relative border-2 border-teal-300 rounded-lg overflow-hidden bg-white shadow-sm">
                    <img src={getImageUrl(d.cad_image)} alt="CAD" className="max-w-[180px] max-h-[180px] object-contain p-2" />
                  </div>
                  {d.cad_link && <a href={d.cad_link.startsWith('http') ? d.cad_link : `https://${d.cad_link}`} target="_blank" rel="noreferrer" className="text-[10px] text-teal-600 hover:underline flex items-center gap-1 mt-1 justify-center"><Link size={10} />CAD File</a>}
                </div>
              )}
            </div>

            {/* CAD Versions — clickable to show full sheet */}
            {d.cad_versions?.length > 0 && (
              <Section title="CAD Versions" defaultOpen={true} badge={`${d.cad_versions.length} version${d.cad_versions.length > 1 ? 's' : ''}`}>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[...d.cad_versions].reverse().map((v, i) => (
                    <button
                      key={i}
                      onClick={async () => {
                        if (v.sheet_id) {
                          try {
                            const res = await getCadSheet(v.sheet_id);
                            setCadSheetPreview(res.data);
                          } catch { setCadSheetPreview({ _fallback: true, cad_image: v.cad_image, version: v.version || d.cad_versions.length - i }); }
                        } else if (v.cad_image) {
                          setCadSheetPreview({ _fallback: true, cad_image: v.cad_image, version: v.version || d.cad_versions.length - i });
                        }
                      }}
                      className={`border rounded-lg p-2 text-left cursor-pointer transition-all hover:ring-2 hover:ring-teal-400 ${i === 0 ? 'bg-teal-50 border-teal-200' : 'bg-slate-50 hover:bg-slate-100'}`}
                      data-testid={`cad-version-${i}`}
                    >
                      {v.cad_image && <img src={getImageUrl(v.cad_image)} alt={`V${v.version || d.cad_versions.length - i}`} className="w-full h-24 object-contain rounded bg-white mb-1" />}
                      <p className="text-[10px] text-muted-foreground">V{v.version || d.cad_versions.length - i} {i === 0 ? '(latest)' : ''} {v.updated_at ? `\u2014 ${new Date(v.updated_at).toLocaleDateString()}` : ''}</p>
                      {v.cad_link && <span className="text-[10px] text-teal-600"><Link size={9} className="inline mr-0.5" />File</span>}
                      {v.designer_name && <p className="text-[10px] text-muted-foreground">{v.designer_name}</p>}
                      {v.view_count > 0 && <p className="text-[10px] text-teal-600 font-medium">{v.view_count} views — click to preview</p>}
                      {!v.view_count && <p className="text-[10px] text-muted-foreground">Click to view</p>}
                    </button>
                  ))}
                </div>
              </Section>
            )}

            {/* Basic Info */}
            <Section title="Basic Information" defaultOpen={true}>
              {editMode ? (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <EditField label="Style No" value={d.style_no} onChange={v => updateField('style_no', v)} placeholder="R2-214216" />
                  <EditField label="Customer CAD No" value={d.party_cad_no} onChange={v => updateField('party_cad_no', v)} />
                  <EditField label="Customer Design No" value={d.customer_design_no} onChange={v => updateField('customer_design_no', v)} placeholder="From SGI / portal" />
                  <EditField label="Product Name" value={d.product_name} onChange={v => updateField('product_name', v)} />
                  <div>
                    <Label className="text-xs text-muted-foreground">Client Name</Label>
                    <ClientSelect value={d.client_name} onChange={v => updateField('client_name', v)} placeholder="Select client..." className="w-full mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">KS Design No</Label>
                    <Input value={d.ks_design_no || ''} disabled className="w-full mt-1 bg-muted cursor-not-allowed h-9 text-xs font-mono font-semibold" />
                  </div>
                  <EditField label="Piece Type" value={d.piece_type} onChange={v => updateField('piece_type', v)} options={PIECE_TYPES} />
                  <EditField label="Size" value={d.size} onChange={v => updateField('size', v)} />
                  <EditField label="Quantity" value={d.quantity} onChange={v => updateField('quantity', v)} type="number" />
                  <EditField label="Priority" value={d.priority || 'normal'} onChange={v => updateField('priority', v)} options={[{value:'normal',label:'Normal'},{value:'super_rush',label:'SUPER RUSH ⚠️'}]} />
                  <EditField label="Deadline" value={d.deadline?.split('T')[0]} onChange={v => updateField('deadline', v)} type="date" />
                  <EditField label="CAD File Name" value={d.cad_file_name} onChange={v => updateField('cad_file_name', v)} />
                  <EditField label="CAD Link" value={d.cad_url} onChange={v => updateField('cad_url', v)} placeholder="Z:\CAD Files\..." />
                  <EditField label="End Date" value={d.end_date ? d.end_date.split('T')[0] : ''} onChange={v => updateField('end_date', v)} type="date" />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2">
                  <Field label="Style No" value={d.style_no} />
                  <Field label="Customer CAD No" value={d.party_cad_no} />
                  <Field label="Customer Design No" value={d.customer_design_no} />
                  <Field label="Product Name" value={d.product_name} />
                  <Field label="Client" value={d.client_name} />
                  <Field label="KS Design No" value={d.ks_design_no} />
                  <Field label="Piece Type" value={PIECE_TYPES.find(p => p.value === d.piece_type)?.label || d.piece_type} />
                  <Field label="Size" value={d.size} />
                  <Field label="Quantity" value={d.quantity} />
                  <Field label="Priority" value={(d.priority === 'super_rush' || d.priority === 'urgent') ? '⚠️ SUPER RUSH' : 'Normal'} />
                  <Field label="Deadline" value={d.deadline ? new Date(d.deadline).toLocaleDateString() : null} />
                  <Field label="CAD File Name" value={d.cad_file_name} />
                  {d.cad_url && (
                    <div>
                      <span className="text-[10px] text-muted-foreground font-medium block">CAD File Location</span>
                      <span className="text-xs text-foreground font-mono bg-muted/50 px-1.5 py-0.5 rounded truncate block" data-testid="cad-url-display">{d.cad_url}</span>
                    </div>
                  )}
                  <Field label="End Date" value={d.end_date ? new Date(d.end_date).toLocaleDateString() : 'Auto'} />
                  <Field label="Internal CAD No" value={d.internal_cad_no} />
                </div>
              )}
            </Section>

            {/* Metal Details */}
            <Section title="Metal(s) & Weights">
              {editMode ? (
                <div>
                  {/* Primary metal as row */}
                  <div className="flex items-center gap-2 mb-2">
                    <Select value={d.metal_type || ''} onValueChange={v => updateField('metal_type', v)}>
                      <SelectTrigger className="h-7 text-xs w-[90px]"><SelectValue placeholder="Type" /></SelectTrigger>
                      <SelectContent>{METAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={d.metal_karat || ''} onValueChange={v => updateField('metal_karat', v)}>
                      <SelectTrigger className="h-7 text-xs w-[80px]"><SelectValue placeholder="Karat" /></SelectTrigger>
                      <SelectContent>{metalKarats.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={d.metal_colour || ''} onValueChange={v => updateField('metal_colour', v)}>
                      <SelectTrigger className="h-7 text-xs w-[90px]"><SelectValue placeholder="Color" /></SelectTrigger>
                      <SelectContent>{metalColors.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-[10px] text-muted-foreground">Primary</span>
                  </div>
                  {/* Additional metals */}
                  {(d.metal_details || []).map((metal, idx) => (
                    <div key={`metal-edit-${idx}-${metal.metalType || ''}`} className="flex items-center gap-2 mb-1.5">
                      <Select value={metal.metal_type?.toLowerCase() || 'gold'} onValueChange={v => {
                        const details = [...(d.metal_details || [])];
                        details[idx] = { ...details[idx], metal_type: v.charAt(0).toUpperCase() + v.slice(1), metal_colour: (METAL_COLOURS[v] || ['Yellow'])[0], metal_karat: (METAL_KARATS[v] || ['14K'])[0] };
                        updateField('metal_details', details);
                      }}>
                        <SelectTrigger className="h-7 text-xs w-[90px]"><SelectValue /></SelectTrigger>
                        <SelectContent>{METAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={metal.metal_karat || '14K'} onValueChange={v => {
                        const details = [...(d.metal_details || [])];
                        details[idx] = { ...details[idx], metal_karat: v };
                        updateField('metal_details', details);
                      }}>
                        <SelectTrigger className="h-7 text-xs w-[80px]"><SelectValue /></SelectTrigger>
                        <SelectContent>{(METAL_KARATS[metal.metal_type?.toLowerCase() || 'gold'] || ['14K']).map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={metal.metal_colour || 'Yellow'} onValueChange={v => {
                        const details = [...(d.metal_details || [])];
                        details[idx] = { ...details[idx], metal_colour: v };
                        updateField('metal_details', details);
                      }}>
                        <SelectTrigger className="h-7 text-xs w-[90px]"><SelectValue /></SelectTrigger>
                        <SelectContent>{(METAL_COLOURS[metal.metal_type?.toLowerCase() || 'gold'] || ['Yellow']).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input className="h-7 text-xs w-[80px]" value={metal.weight || ''} onChange={e => {
                        const details = [...(d.metal_details || [])];
                        details[idx] = { ...details[idx], weight: parseFloat(e.target.value) || null };
                        updateField('metal_details', details);
                      }} placeholder="Wt (g)" type="number" step="0.00001" />
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => {
                        updateField('metal_details', (d.metal_details || []).filter((_, i) => i !== idx));
                      }}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  ))}
                  {(d.metal_details || []).length < 2 && (
                    <Button variant="outline" size="sm" onClick={() => updateField('metal_details', [...(d.metal_details || []), { metal_type: 'Gold', metal_colour: 'Yellow', metal_karat: '18K', weight: null }])} className="h-6 text-xs px-2 mt-1">
                      <Plus size={12} className="mr-1" />Add Metal
                    </Button>
                  )}
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-2 border-t border-border/50">
                    <EditField label="Net Weight (g)" value={d.net_weight} onChange={v => updateField('net_weight', v)} type="number" step="0.00001" />
                    <EditField label="Gross Weight (g)" value={d.gross_weight} onChange={v => updateField('gross_weight', v)} type="number" step="0.00001" />
                    <EditField label="Finishing" value={d.finishing || d.texture} onChange={v => updateField('finishing', v)} placeholder="e.g. High Polish" />
                  </div>
                </div>
              ) : (
                <div>
                  {/* Primary metal row */}
                  <div className="flex items-center gap-3 text-sm mb-1">
                    {d.metal_type && <span className="capitalize font-medium">{d.metal_type}</span>}
                    {d.metal_karat && <span className="text-muted-foreground">{d.metal_karat}</span>}
                    {d.metal_colour && <span className="text-muted-foreground">{d.metal_colour}</span>}
                  </div>
                  {/* Additional metals */}
                  {(d.metal_details || []).map((metal, idx) => (
                    <div key={`metal-view-${idx}-${metal.metalType || ''}`} className="flex items-center gap-3 text-sm text-muted-foreground mb-0.5">
                      <span>{metal.metal_type}</span>
                      <span>{metal.metal_karat}</span>
                      <span>{metal.metal_colour}</span>
                      {metal.weight && <span>{metal.weight}g</span>}
                    </div>
                  ))}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-2 mt-2 pt-2 border-t border-border/50">
                    <Field label="Net Weight" value={d.net_weight ? `${d.net_weight}g` : null} />
                    <Field label="Gross Weight" value={d.gross_weight ? `${d.gross_weight}g` : null} />
                    <Field label="Total Weight" value={totalWeight ? `${totalWeight}g` : null} className={totalWeight ? 'font-semibold text-primary' : ''} />
                    <Field label="Finishing" value={d.finishing || d.texture} />
                  </div>
                </div>
              )}
            </Section>

            {/* Findings */}
            {(pieceFindings.length > 0 || (d.findings && d.findings.length > 0)) && (
              <Section title="Findings / Components" badge={d.findings?.length ? `${d.findings.length} items | ${totalFindingsWeight.toFixed(2)}g` : null}>
                {editMode ? (
                  <div>
                    {(d.findings || []).map((f, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        <Select value={f.code || ''} onValueChange={v => updateFinding(idx, 'code', v)}>
                          <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Select finding..." /></SelectTrigger>
                          <SelectContent>{pieceFindings.filter(pf => pf.code !== 'NONE').map(pf => <SelectItem key={pf.code} value={pf.code}>{pf.name}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className="h-7 text-xs w-20" value={f.custom_name || ''} onChange={e => updateFinding(idx, 'custom_name', e.target.value)} placeholder="Name (e.g. ANC30)" />
                        <Input className="h-7 text-xs w-20" type="number" step="0.00001" min="0" value={f.weight || ''} onChange={e => updateFinding(idx, 'weight', e.target.value)} placeholder="Weight" />
                        <Input className="h-7 text-xs w-24" value={f.details || ''} onChange={e => updateFinding(idx, 'details', e.target.value)} placeholder="Details" />
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => removeFinding(idx)}>
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    ))}
                    <Button variant="outline" size="sm" onClick={addFinding} className="h-7 text-xs" data-testid="add-finding-btn">
                      <Plus size={12} className="mr-1" />Add Finding
                    </Button>
                  </div>
                ) : (
                  <div>
                    {(d.findings || []).length === 0 ? (
                      <p className="text-xs text-muted-foreground italic">No findings added</p>
                    ) : (
                      <div className="space-y-1">
                        {d.findings.map((f, i) => (
                          <div key={i} className="flex items-center justify-between text-sm py-1 border-b border-border/30 last:border-0">
                            <span>{f.name || f.code}{f.custom_name ? ` (${f.custom_name})` : ''}</span>
                            <span className="text-muted-foreground">{f.weight}g{f.details ? ` \u2014 ${f.details}` : ''}</span>
                          </div>
                        ))}
                        <p className="text-xs text-muted-foreground pt-1">Total findings weight: {totalFindingsWeight.toFixed(2)}g</p>
                      </div>
                    )}
                  </div>
                )}
              </Section>
            )}

            {/* Stone Details */}
            <Section title="Stone Details" badge={d.diamonds?.length ? `${d.diamonds.length} stones | ${totalDiamondWeight.toFixed(2)}ct` : null}>
              {editMode ? (
                <div>
                  {(d.diamonds || []).length > 0 && (
                    <div className="overflow-x-auto mb-2">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Stone</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Ownership</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Cut</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Category</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Quality</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Size</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Ct. Wt</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Qty</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Total</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(d.diamonds || []).map((st, idx) => (
                            <tr key={st.id || `stone-edit-${idx}`} className="border-b border-border/30">
                              <td className="py-0.5 px-0.5">
                                {['Natural', 'Lab', 'CVD', 'Colour Stone'].includes(st.stone_category) ? (
                                  <Select value={st.stone_category || 'Natural'} onValueChange={v => { if (v === '__other') updateStone(idx, 'stone_category', ''); else updateStone(idx, 'stone_category', v); }}>
                                    <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[80px]"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {['Natural', 'Lab', 'CVD', 'Colour Stone'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                      <SelectItem value="__other">Other...</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input className="h-6 text-[11px] p-1 w-[80px]" value={st.stone_category || ''} onChange={e => updateStone(idx, 'stone_category', e.target.value)} placeholder="Type..." />
                                )}
                              </td>
                              <td className="py-0.5 px-0.5">
                                {['KS Goods', 'Party Goods'].includes(st.ownership) ? (
                                  <Select value={st.ownership || 'KS Goods'} onValueChange={v => { if (v === '__other') updateStone(idx, 'ownership', ''); else updateStone(idx, 'ownership', v); }}>
                                    <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[80px]"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="KS Goods">KS Goods</SelectItem>
                                      <SelectItem value="Party Goods">Party Goods</SelectItem>
                                      <SelectItem value="__other">Other...</SelectItem>
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input className="h-6 text-[11px] p-1 w-[80px]" value={st.ownership || ''} onChange={e => updateStone(idx, 'ownership', e.target.value)} placeholder="Type..." />
                                )}
                              </td>
                              <td className="py-0.5 px-0.5">
                                {STONE_SHAPES.includes(st.stone_shape) ? (
                                  <Select value={st.stone_shape || 'Round'} onValueChange={v => { if (v === '__other') updateStone(idx, 'stone_shape', ''); else updateStone(idx, 'stone_shape', v); }}>
                                    <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[70px]"><SelectValue /></SelectTrigger>
                                    <SelectContent>{STONE_SHAPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}<SelectItem value="__other">Other...</SelectItem></SelectContent>
                                  </Select>
                                ) : (
                                  <Input className="h-6 text-[11px] p-1 w-[70px]" value={st.stone_shape || ''} onChange={e => updateStone(idx, 'stone_shape', e.target.value)} placeholder="Cut..." />
                                )}
                              </td>
                              <td className="py-0.5 px-0.5">
                                <Select value={st.category || 'Accent Stone'} onValueChange={v => updateStone(idx, 'category', v)}>
                                  <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[70px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="Center Stone">Center</SelectItem>
                                    <SelectItem value="Accent Stone">Accent</SelectItem>
                                    <SelectItem value="Side Stone">Side</SelectItem>
                                  </SelectContent>
                                </Select>
                              </td>
                              <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" value={st.stone_quality || ''} onChange={e => updateStone(idx, 'stone_quality', e.target.value)} placeholder="VS1" /></td>
                              <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" value={st.size_mm || ''} onChange={e => updateStone(idx, 'size_mm', e.target.value)} placeholder="mm" /></td>
                              <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" type="number" step="0.00001" min="0" value={st.weight_per_stone || ''} onChange={e => updateStone(idx, 'weight_per_stone', parseFloat(e.target.value) || 0)} placeholder="ct" /></td>
                              <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-12" type="number" value={st.quantity || ''} onChange={e => updateStone(idx, 'quantity', parseInt(e.target.value) || 0)} /></td>
                              <td className="py-0.5 px-0.5 text-center text-[11px] text-muted-foreground">{(st.total_weight || 0).toFixed(2)}</td>
                              <td><Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => removeStone(idx)}><Trash2 size={10} /></Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={addStone} className="h-7 text-xs" data-testid="add-stone-btn"><Plus size={12} className="mr-1" />Add Stone</Button>
                    <Button variant="outline" size="sm" onClick={() => setShowBulkStoneDialog(true)} className="h-7 text-xs" data-testid="bulk-add-stone-btn"><Grid size={12} className="mr-1" />Bulk Import</Button>
                    <label>
                      <Button variant="outline" size="sm" className="h-7 text-xs" asChild><span><Upload size={12} className="mr-1" />Import Excel</span></Button>
                      <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleStoneUpload} />
                    </label>
                  </div>
                </div>
              ) : (
                <div>
                  {(d.diamonds || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No stones added</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Stone</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Ownership</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Cut</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Category</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Quality</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Size</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Ct. Wt</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Qty</th>
                            <th className="text-left py-1 px-1 font-medium text-muted-foreground">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(d.diamonds || []).map((st, idx) => (
                            <tr key={st.id || `stone-view-${idx}`} className="border-b border-border/30">
                              <td className="py-1 px-1">{st.stone_category || '—'}</td>
                              <td className="py-1 px-1">{st.ownership || '—'}</td>
                              <td className="py-1 px-1">{st.stone_shape || '—'}</td>
                              <td className="py-1 px-1">{st.category || 'Accent'}</td>
                              <td className="py-1 px-1">{st.stone_quality || '—'}</td>
                              <td className="py-1 px-1">{st.size_mm || '—'}</td>
                              <td className="py-1 px-1">{st.weight_per_stone || '—'}</td>
                              <td className="py-1 px-1">{st.quantity || '—'}</td>
                              <td className="py-1 px-1">{(st.total_weight || 0).toFixed(2)}ct</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-xs text-muted-foreground pt-1 mt-1 border-t border-border/50">Total: {totalDiamondWeight.toFixed(2)}ct</p>
                    </div>
                  )}
                </div>
              )}
            </Section>

            {/* Engraving, Stamping & Instructions */}
            <Section title="Engraving & Instructions" defaultOpen={!!(d.engraving || d.special_instructions || d.other_details || d.stamping_instructions)}>
              {editMode ? (
                <div className="space-y-3">
                  <EditField label="Engraving" value={d.engraving} onChange={v => updateField('engraving', v)} type="textarea" />
                  <div>
                    <span className="text-[10px] text-muted-foreground font-medium block mb-1">Stamping Logo</span>
                    <div className="flex items-center gap-2">
                      {d.stamping_images?.[0] && <img src={getImageUrl(d.stamping_images[0])} alt="Stamp" className="w-14 h-14 rounded border object-contain" />}
                      <label className="cursor-pointer">
                        <span className="text-xs text-primary hover:underline">{d.stamping_images?.[0] ? 'Change' : 'Upload'} Logo</span>
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file) return;
                          try { const res = await uploadImage(file); updateField('stamping_images', [res.data.image]); toast.success('Stamping logo uploaded'); } catch { toast.error('Upload failed'); }
                          e.target.value = '';
                        }} />
                      </label>
                    </div>
                  </div>
                  <EditField label="Stamping Instructions" value={d.stamping_instructions} onChange={v => updateField('stamping_instructions', v)} type="textarea" placeholder="Stamping details..." />
                  <EditField label="Special Instructions" value={d.special_instructions} onChange={v => updateField('special_instructions', v)} type="textarea" />
                  <EditField label="Other Details" value={d.other_details} onChange={v => updateField('other_details', v)} type="textarea" />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2">
                  <Field label="Engraving" value={d.engraving} />
                  {(d.stamping_images?.[0] || d.stamping_instructions) && (
                    <div className="flex items-start gap-3 mt-1">
                      {d.stamping_images?.[0] && (
                        <div>
                          <span className="text-[10px] text-muted-foreground font-medium block mb-0.5">Stamping Logo</span>
                          <img src={getImageUrl(d.stamping_images[0])} alt="Stamping" className="w-20 h-20 rounded border object-contain bg-white" />
                        </div>
                      )}
                      {d.stamping_instructions && (
                        <div className="flex-1">
                          <span className="text-[10px] text-muted-foreground font-medium block mb-0.5">Stamping Instructions</span>
                          <p className="text-xs text-foreground">{d.stamping_instructions}</p>
                        </div>
                      )}
                    </div>
                  )}
                  <Field label="Special Instructions" value={d.special_instructions} />
                  <Field label="Other Details" value={d.other_details} />
                  {!d.engraving && !d.special_instructions && !d.other_details && !d.stamping_instructions && (
                    <p className="text-xs text-muted-foreground italic">No instructions added</p>
                  )}
                </div>
              )}
            </Section>

            {/* Notes — hidden when the structured image_notes table is showing,
                since that already surfaces the per-image notes and the flat
                blob would just duplicate the content. In edit mode the section
                stays visible so admins can still add freeform notes; otherwise
                it only renders when there's actual text to show. */}
            {(editMode || (d.notes && !d.image_notes?.length)) && (
              <Section title="Notes" defaultOpen={!!d.notes}>
                {editMode ? (
                  <EditField label="Notes" value={d.notes} onChange={v => updateField('notes', v)} type="textarea" />
                ) : (
                  <div className="text-sm text-foreground whitespace-pre-wrap">{d.notes || <span className="text-muted-foreground italic">No notes</span>}</div>
                )}
              </Section>
            )}

            {/* CAD URL & Links */}
            {(d.cad_url || editMode) && (
              <Section title="CAD Link" defaultOpen={!!d.cad_url}>
                {editMode ? (
                  <EditField label="CAD URL" value={d.cad_url} onChange={v => updateField('cad_url', v)} placeholder="https://..." />
                ) : d.cad_url ? (
                  <a href={d.cad_url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline flex items-center gap-1"><Link size={14} />{d.cad_url}</a>
                ) : null}
              </Section>
            )}

            {(editMode || d.ks_notes) && (
              <Section title="KS Notes" defaultOpen={!!d.ks_notes}>
                {editMode ? (
                  <EditField label="KS Notes" value={d.ks_notes} onChange={v => updateField('ks_notes', v)} type="textarea" placeholder="Add KS Notes here..." />
                ) : (
                  <div className="text-sm text-foreground whitespace-pre-wrap">{d.ks_notes || <span className="text-muted-foreground italic">No KS Notes</span>}</div>
                )}
              </Section>
            )}
          </div>
        )}

        {activeTab === 'client' && (
          <div>
            {/* Images Uploaded — SGI-style per-image table (image · notes · date).
                Rendered when `image_notes` is populated (SGI imports + future
                structured uploads). The legacy "Reference Images" grid below
                stays for tasks that don't have the structured data. */}
            {(d.image_notes?.length > 0) && (
              <Section title="Images Uploaded" badge={d.image_notes.length} defaultOpen={true}>
                <ImagesUploadedTable entries={d.image_notes} />
              </Section>
            )}

            {/* CAD Image (Main Reference) — shown to clients in edit mode */}
            <Section title="CAD Image (Main Reference)" defaultOpen={true}>
              {d.product_image && (
                <div className="mb-3 relative inline-block">
                  <img src={getImageUrl(d.product_image)} alt="Product" className="w-28 h-28 object-contain rounded border border-border bg-white" />
                  {editMode && (
                    <button
                      onClick={() => updateField('product_image', '')}
                      className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px]"
                    >&times;</button>
                  )}
                </div>
              )}
              {editMode && (
                <label className="inline-flex items-center gap-1 mt-1 px-3 py-1.5 border border-dashed border-border rounded cursor-pointer hover:bg-muted/30 text-xs text-muted-foreground">
                  <Upload size={12} /> {d.product_image ? 'Replace Image' : 'Upload Image'}
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e, 'product_image')} />
                </label>
              )}
              {!d.product_image && !editMode && (
                <p className="text-xs text-muted-foreground italic">No image attached</p>
              )}
            </Section>

            {/* CAD File Upload — shown to clients in edit mode */}
            <Section title="CAD File (Primary .3dm / .stl)" defaultOpen={true}>
              {d.cad_file && (
                <div className="flex items-center gap-2 mb-2 text-sm text-teal-600">
                  <a href={getImageUrl(d.cad_file)} target="_blank" rel="noopener noreferrer" className="underline font-mono text-xs">
                    View Uploaded CAD File
                  </a>
                  {editMode && (
                    <button onClick={() => updateField('cad_file', '')} className="text-destructive text-[10px] hover:underline">&times; Remove</button>
                  )}
                </div>
              )}
              {editMode && (
                <label className="inline-flex items-center gap-1 mt-1 px-3 py-1.5 border border-dashed border-border rounded cursor-pointer hover:bg-muted/30 text-xs text-muted-foreground">
                  <Upload size={12} /> {d.cad_file ? 'Replace CAD File' : 'Upload CAD File'}
                  <input type="file" accept=".3dm,.stl,.obj,.dxf,.dwg" className="hidden" onChange={(e) => handleFileUpload(e, 'cad_file')} />
                </label>
              )}
              {!d.cad_file && !editMode && (
                <p className="text-xs text-muted-foreground italic">No CAD file attached</p>
              )}
            </Section>

            {/* Reference Images — legacy flat grid. Auto-collapses when the
                structured `image_notes` table is rendering above. */}
            <Section title="Reference Images" badge={d.reference_images?.length || null} defaultOpen={!d.image_notes?.length && !!(d.reference_images?.length)}>
              <div className="flex flex-wrap gap-2">
                {(d.reference_images || []).map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={getImageUrl(img)} alt={`Ref ${i + 1}`} className="w-20 h-20 object-contain rounded border border-border bg-white" />
                    {editMode && (
                      <button
                        onClick={() => updateField('reference_images', (d.reference_images || []).filter((_, j) => j !== i))}
                        className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100 transition-opacity"
                      >&times;</button>
                    )}
                  </div>
                ))}
              </div>
              {editMode && (
                <label className="inline-flex items-center gap-1 mt-2 px-2 py-1 border border-dashed border-border rounded cursor-pointer hover:bg-muted/30 text-xs text-muted-foreground">
                  <Upload size={12} /> Add Reference Images
                  <input type="file" accept="image/*" multiple className="hidden" onChange={handleRefImageUpload} />
                </label>
              )}
              {!(d.reference_images?.length) && !editMode && (
                <p className="text-xs text-muted-foreground italic">No reference images attached</p>
              )}
            </Section>
          </div>
        )}

        {activeTab === 'workflow' && (
          <div>
            {/* Designer & CAD Points & Time Tracking */}
            <Section title="Designer & CAD Points" defaultOpen={true}>
              {editMode ? (
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  <div>
                    <span className="text-[10px] text-muted-foreground font-medium block mb-1">Executor (Designer)</span>
                    <Select value={d.designer_id || ''} onValueChange={v => {
                      const found = executors.find(ex => ex.id === v);
                      updateField('designer_id', v);
                      updateField('designer_name', found ? found.name : v);
                      updateField('designer_hashtag', '');
                    }} disabled={isExecutorOrFinalExecutor}>
                      <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select executor..." /></SelectTrigger>
                      <SelectContent>
                        {executors.map(ex => (
                          <SelectItem key={ex.id} value={ex.id}>{ex.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <EditField label="Design Points" value={d.design_points} onChange={v => updateField('design_points', v)} type="number" placeholder="Points" disabled={isExecutorOrFinalExecutor} />
                  <EditField label="CAD Difficulty" value={d.cad_difficulty} onChange={v => updateField('cad_difficulty', v)} options={[{value:'simple',label:'Simple'},{value:'moderate',label:'Moderate'},{value:'complex',label:'Complex'},{value:'expert',label:'Expert'}]} disabled={isExecutorOrFinalExecutor} />
                  <EditField label="CAD Points (hrs)" value={d.cad_points} onChange={v => updateField('cad_points', v)} type="number" disabled={isExecutorOrFinalExecutor} />
                  <EditField label="CAD Start Time" value={toLocalISOString(d.cad_start_time)} onChange={v => updateField('cad_start_time', v ? parseDateTime(v).toISOString() : '')} type="datetime-local" disabled={isExecutorOrFinalExecutor} />
                  <EditField label="CAD Completion Time" value={toLocalISOString(d.cad_completion_time)} onChange={v => {
                    const isoVal = v ? parseDateTime(v).toISOString() : '';
                    updateField('cad_completion_time', isoVal);
                    // Auto-calculate actual hours
                    if (v && d.cad_start_time) {
                      const start = parseDateTime(d.cad_start_time);
                      const end = parseDateTime(v);
                      if (start && end) {
                        const hours = ((end - start) / 3600000).toFixed(2);
                        if (hours > 0) updateField('actual_time_hours', parseFloat(hours));
                      }
                    }
                  }} type="datetime-local" disabled={isExecutorOrFinalExecutor} />
                  <EditField label="Actual Time (hrs)" value={d.actual_time_hours} onChange={v => updateField('actual_time_hours', v)} type="number" disabled={isExecutorOrFinalExecutor} />
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2">
                  <Field label="Designer" value={d.designer_name ? `${d.designer_name} (${d.designer_hashtag || ''})` : null} />
                  <Field label="Design Points" value={d.design_points} />
                  <Field label="CAD Difficulty" value={d.cad_difficulty ? d.cad_difficulty.charAt(0).toUpperCase() + d.cad_difficulty.slice(1) : null} />
                  <Field label="CAD Points (hrs)" value={d.cad_points} />
                  <Field label="CAD Start Time" value={d.cad_start_time ? formatDateTime(d.cad_start_time) : null} />
                  <Field label="CAD Completion Time" value={d.cad_completion_time ? formatDateTime(d.cad_completion_time) : null} />
                  <Field label="Actual Time (hrs)" value={d.actual_time_hours ? `${d.actual_time_hours} hrs` : null} />
                  {d.cad_points && d.actual_time_hours ? (
                    <div>
                      <span className="text-[10px] text-muted-foreground font-medium block">Efficiency</span>
                      <p className={`text-sm font-bold mt-0.5 ${(d.cad_points / d.actual_time_hours) >= 1 ? 'text-green-600' : 'text-amber-600'}`}>
                        {((d.cad_points / d.actual_time_hours) * 100).toFixed(0)}%
                      </p>
                    </div>
                  ) : null}
                </div>
              )}
            </Section>

            {/* Workflow Timeline */}
            <Section title="Workflow Timeline" defaultOpen={true}>
              <div className="space-y-3">
                {[
                  { status: 'cad_in_progress', label: 'CAD Created', icon: <FileText size={14} />, date: d.created_at },
                  { status: 'sent_to_client', label: 'Sent to Client', icon: <Send size={14} />, date: d.sent_to_client_at },
                  { status: 'approved', label: 'Approved', icon: <ThumbsUp size={14} />, date: d.client_approved_at },
                  { status: 'sampling', label: 'Sampling', icon: <Check size={14} />, date: d.sampling_at },
                  { status: 'completed', label: 'Completed', icon: <Check size={14} />, date: d.completed_at },
                ].map((step, idx) => {
                  const isCurrent = currentStatus === step.status;
                  const statusOrder = ['cad_in_progress', 'sent_to_client', 'approved', 'sampling', 'completed'];
                  const isPast = statusOrder.indexOf(currentStatus) >= idx;
                  const isRevision = currentStatus === 'revision_requested';
                  return (
                    <div key={step.status} className="flex items-start gap-3">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isCurrent ? 'bg-primary text-primary-foreground' :
                        isPast && !isRevision ? 'bg-green-100 text-green-700' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {step.icon}
                      </div>
                      <div>
                        <p className={`text-sm ${isCurrent ? 'font-semibold text-foreground' : isPast ? 'text-foreground' : 'text-muted-foreground'}`}>{step.label}</p>
                        {step.date && <p className="text-[10px] text-muted-foreground">{new Date(step.date).toLocaleString()}</p>}
                      </div>
                    </div>
                  );
                })}
                {currentStatus === 'revision_requested' && (
                  <div className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 bg-rose-100 text-rose-700">
                      <RotateCcw size={14} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-rose-700">Revision Requested</p>
                      {d.client_revisions?.length > 0 && (
                        <>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{d.client_revisions[d.client_revisions.length - 1]?.remarks}</p>
                          {(d.client_revisions[d.client_revisions.length - 1]?.images || []).length > 0 && (
                            <div className="flex gap-1.5 mt-1.5 flex-wrap">
                              {(d.client_revisions[d.client_revisions.length - 1]?.images || []).filter(Boolean).map((img, i) => (
                                <a key={i} href={getImageUrl(img)} target="_blank" rel="noreferrer">
                                  <img src={getImageUrl(img)} alt={`rev-img-${i}`} className="w-12 h-12 rounded border object-cover hover:ring-2 ring-rose-300 transition-all" />
                                </a>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Section>

            {/* QC Signoffs */}
            {(d.workflow_signoffs || []).length > 0 && (
              <Section title="Signoff History">
                <div className="space-y-2">
                  {d.workflow_signoffs.map((s, i) => (
                    <div key={i} className="text-xs p-2 bg-muted/30 rounded">
                      <p className="font-medium">{s.signed_by_name} \u2014 {s.stage}</p>
                      <p className="text-muted-foreground">{s.remarks}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(s.signed_at).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Revision History */}
            {revisions.length > 0 && (
              <Section title="Revision History" badge={revisions.length}>
                <div className="space-y-1">
                  {revisions.map(rev => (
                    <div key={rev.id} className={`flex items-center justify-between text-xs px-2 py-1.5 rounded ${rev.id === task.id ? 'bg-primary/10 border border-primary/20' : 'border border-border/30'}`}>
                      <div>
                        <span className="font-medium">{rev.style_no}</span>
                        {rev.id === task.id && <span className="text-[10px] text-primary ml-1">(current)</span>}
                      </div>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] border ${STATUS_COLORS[rev.design_status] || 'bg-muted'}`}>
                        {STATUS_LABELS[rev.design_status] || rev.design_status}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Client Revision Details (remarks + images) */}
            {(d.client_revisions || []).length > 0 && (
              <Section title="Client Revision Notes" badge={d.client_revisions.length} defaultOpen={true}>
                <div className="space-y-3">
                  {d.client_revisions.map((cr, i) => (
                    <div key={i} className="p-2.5 bg-rose-50/50 border border-rose-100 rounded-lg" data-testid={`client-revision-${cr.revision_number}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-semibold text-rose-700">Rev #{cr.revision_number}</span>
                        <span className="text-[10px] text-muted-foreground">{cr.requested_at ? new Date(cr.requested_at).toLocaleString() : ''}</span>
                      </div>
                      <p className="text-xs text-foreground">{cr.remarks}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">By: {cr.requested_by_name}</p>
                      {(cr.images || []).length > 0 && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {cr.images.map((img, j) => (
                            <a key={j} href={getImageUrl(img)} target="_blank" rel="noreferrer">
                              <img src={getImageUrl(img)} alt={`cr-${i}-${j}`} className="w-14 h-14 rounded border object-cover hover:ring-2 ring-rose-300 transition-all" />
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Assignment Info */}
            <Section title="Assignment" defaultOpen={true}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <Field label="Created By" value={d.created_by_name} />
                <Field label="Assigned Customer" value={d.assigned_customer_name} />
                <Field label="Created At" value={d.created_at ? new Date(d.created_at).toLocaleString() : null} />
                <Field label="Last Updated" value={d.updated_at ? new Date(d.updated_at).toLocaleString() : null} />
              </div>
            </Section>
          </div>
        )}
      </div>

      {/* Assign Customer Dialog */}
      {showAssignCustomer && (
        <AssignCustomerDialog
          taskId={task.id}
          onClose={() => setShowAssignCustomer(false)}
          onAssigned={() => { setShowAssignCustomer(false); onUpdate?.(); }}
        />
      )}


      {/* CAD Sheet Preview Modal */}
      {cadSheetPreview && (
        <CadSheetPreviewModal
          data={cadSheetPreview}
          task={task}
          onClose={() => setCadSheetPreview(null)}
        />
      )}

      {/* Bulk Stone Import Dialog */}
      <TaskStoneImportDialog
        open={showBulkStoneDialog}
        onOpenChange={setShowBulkStoneDialog}
        onImport={handleBulkStoneImport}
        existingStones={d.diamonds || []}
      />
    </div>
  );
};

export default TaskSummaryView;
